import { db } from '../config/firebase';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, orderBy, serverTimestamp, onSnapshot } from 'firebase/firestore';

class WalletService {
  // Platform commission percentage
  static PLATFORM_COMMISSION = 20; // 20%

  // Calculate lawyer's net amount after commission
  static calculateNetAmount(totalAmount) {
    const commission = (totalAmount * this.PLATFORM_COMMISSION) / 100;
    return totalAmount - commission;
  }

  // Calculate commission amount
  static calculateCommission(totalAmount) {
    return (totalAmount * this.PLATFORM_COMMISSION) / 100;
  }

  // Create or update lawyer wallet
  async createLawyerWallet(lawyerId, initialBalance = 0) {
    try {
      const walletData = {
        lawyerId,
        availableBalance: initialBalance,
        pendingBalance: 0,
        totalEarnings: 0,
        totalCommission: 0,
        totalSettlements: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'lawyer_wallets'), walletData);
      
      return {
        success: true,
        walletId: docRef.id,
        data: { ...walletData, walletId: docRef.id }
      };
    } catch (error) {
      console.error('Error creating lawyer wallet:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get lawyer wallet
  async getLawyerWallet(lawyerId) {
    try {
      const q = query(
        collection(db, 'lawyer_wallets'),
        where('lawyerId', '==', lawyerId)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const walletDoc = querySnapshot.docs[0];
        return {
          success: true,
          data: { id: walletDoc.id, ...walletDoc.data() }
        };
      } else {
        // Create wallet if it doesn't exist
        return await this.createLawyerWallet(lawyerId);
      }
    } catch (error) {
      console.error('Error getting lawyer wallet:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add pending amount when booking is accepted
  async addPendingAmount(lawyerId, bookingId, totalAmount, bookingDetails) {
    try {
      const netAmount = WalletService.calculateNetAmount(totalAmount);
      const commission = WalletService.calculateCommission(totalAmount);

      // Get current wallet
      const walletResult = await this.getLawyerWallet(lawyerId);
      if (!walletResult.success) {
        return walletResult;
      }

      const wallet = walletResult.data;
      const newPendingBalance = wallet.pendingBalance + netAmount;
      const newTotalCommission = wallet.totalCommission + commission;

      // Update wallet
      const walletRef = doc(db, 'lawyer_wallets', wallet.id);
      await updateDoc(walletRef, {
        pendingBalance: newPendingBalance,
        totalCommission: newTotalCommission,
        updatedAt: serverTimestamp()
      });

      // Create pending transaction record
      const transactionData = {
        walletId: wallet.id,
        lawyerId,
        bookingId,
        type: 'pending',
        amount: netAmount,
        totalAmount,
        commission,
        description: `Pending settlement for booking ${bookingId}`,
        bookingDetails,
        createdAt: serverTimestamp(),
        status: 'pending'
      };

      const transactionRef = await addDoc(collection(db, 'wallet_transactions'), transactionData);

      return {
        success: true,
        netAmount,
        commission,
        newPendingBalance,
        transactionId: transactionRef.id
      };
    } catch (error) {
      console.error('Error adding pending amount:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Settle amount when consultation is completed
  async settleAmount(lawyerId, bookingId) {
    try {
      // Get current wallet
      const walletResult = await this.getLawyerWallet(lawyerId);
      if (!walletResult.success) {
        return walletResult;
      }

      const wallet = walletResult.data;

      // Find the pending transaction for this booking
      const transactionQuery = query(
        collection(db, 'wallet_transactions'),
        where('lawyerId', '==', lawyerId),
        where('bookingId', '==', bookingId),
        where('type', '==', 'pending')
      );
      const transactionSnapshot = await getDocs(transactionQuery);

      if (transactionSnapshot.empty) {
        return {
          success: false,
          error: 'No pending transaction found for this booking'
        };
      }

      const transactionDoc = transactionSnapshot.docs[0];
      const transaction = transactionDoc.data();
      const settlementAmount = transaction.amount;

      // Update wallet
      const walletRef = doc(db, 'lawyer_wallets', wallet.id);
      await updateDoc(walletRef, {
        pendingBalance: wallet.pendingBalance - settlementAmount,
        availableBalance: wallet.availableBalance + settlementAmount,
        totalSettlements: wallet.totalSettlements + settlementAmount,
        updatedAt: serverTimestamp()
      });

      // Update transaction status
      const transactionRef = doc(db, 'wallet_transactions', transactionDoc.id);
      await updateDoc(transactionRef, {
        type: 'settled',
        status: 'completed',
        settledAt: serverTimestamp()
      });

      // Create settlement transaction record
      const settlementData = {
        walletId: wallet.id,
        lawyerId,
        bookingId,
        type: 'settlement',
        amount: settlementAmount,
        description: `Settlement for booking ${bookingId}`,
        originalTransactionId: transactionDoc.id,
        createdAt: serverTimestamp(),
        status: 'completed'
      };

      await addDoc(collection(db, 'wallet_transactions'), settlementData);

      return {
        success: true,
        settlementAmount,
        newAvailableBalance: wallet.availableBalance + settlementAmount,
        newPendingBalance: wallet.pendingBalance - settlementAmount
      };
    } catch (error) {
      console.error('Error settling amount:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get wallet transactions
  async getWalletTransactions(lawyerId, limit = 50) {
    try {
      const q = query(
        collection(db, 'wallet_transactions'),
        where('lawyerId', '==', lawyerId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const transactions = [];
      querySnapshot.forEach((doc) => {
        transactions.push({ id: doc.id, ...doc.data() });
      });
      
      return {
        success: true,
        data: transactions.slice(0, limit)
      };
    } catch (error) {
      console.error('Error getting wallet transactions:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Subscribe to wallet updates
  subscribeToWalletUpdates(lawyerId, callback) {
    try {
      const q = query(
        collection(db, 'lawyer_wallets'),
        where('lawyerId', '==', lawyerId)
      );
      
      return onSnapshot(q, (querySnapshot) => {
        if (!querySnapshot.empty) {
          const walletDoc = querySnapshot.docs[0];
          const wallet = { id: walletDoc.id, ...walletDoc.data() };
          callback(wallet);
        } else {
          callback(null);
        }
      });
    } catch (error) {
      console.error('Error subscribing to wallet updates:', error);
      return null;
    }
  }

  // Withdraw funds
  async withdrawFunds(lawyerId, amount, bankDetails) {
    try {
      // Get current wallet
      const walletResult = await this.getLawyerWallet(lawyerId);
      if (!walletResult.success) {
        return walletResult;
      }

      const wallet = walletResult.data;

      if (wallet.availableBalance < amount) {
        return {
          success: false,
          error: 'Insufficient available balance'
        };
      }

      // Update wallet
      const walletRef = doc(db, 'lawyer_wallets', wallet.id);
      await updateDoc(walletRef, {
        availableBalance: wallet.availableBalance - amount,
        updatedAt: serverTimestamp()
      });

      // Create withdrawal transaction
      const withdrawalData = {
        walletId: wallet.id,
        lawyerId,
        type: 'withdrawal',
        amount: -amount, // Negative for withdrawal
        description: 'Withdrawal to bank account',
        bankDetails,
        status: 'pending',
        createdAt: serverTimestamp()
      };

      const withdrawalRef = await addDoc(collection(db, 'wallet_transactions'), withdrawalData);

      return {
        success: true,
        withdrawalId: withdrawalRef.id,
        newAvailableBalance: wallet.availableBalance - amount
      };
    } catch (error) {
      console.error('Error withdrawing funds:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get wallet statistics
  async getWalletStatistics(lawyerId) {
    try {
      const walletResult = await this.getLawyerWallet(lawyerId);
      if (!walletResult.success) {
        return walletResult;
      }

      const wallet = walletResult.data;

      // Get monthly earnings
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      const monthlyQuery = query(
        collection(db, 'wallet_transactions'),
        where('lawyerId', '==', lawyerId),
        where('type', '==', 'settlement'),
        where('createdAt', '>=', new Date(currentYear, currentMonth, 1)),
        where('createdAt', '<', new Date(currentYear, currentMonth + 1, 1))
      );
      
      const monthlySnapshot = await getDocs(monthlyQuery);
      let monthlyEarnings = 0;
      monthlySnapshot.forEach((doc) => {
        const transaction = doc.data();
        monthlyEarnings += transaction.amount;
      });

      return {
        success: true,
        data: {
          availableBalance: wallet.availableBalance,
          pendingBalance: wallet.pendingBalance,
          totalEarnings: wallet.totalEarnings,
          totalCommission: wallet.totalCommission,
          totalSettlements: wallet.totalSettlements,
          monthlyEarnings
        }
      };
    } catch (error) {
      console.error('Error getting wallet statistics:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default new WalletService();
